/**
 * Textos (en) de planta — OBR-149: recompte d'existències a l'App: namespace `planta.stockCount`.
 * Tall propi: no es toca `ca.ts`. Les tres llengües han de tenir les mateixes claus (i18n.test.ts) i les mateixes
 * variables. Vocabulari del sector: «Existències», «recompte», mai «Stock».
 */

/**
 * Tipus AMPLE a posta (no `as const`): el tipus literal de TOTS els catàlegs junts passa del que `tsc` pot serialitzar a la declaració
 * (TS7056 a `i18n/index.ts`) i només aquest espai en porta 139 claus. Les claus es comproven igualment: `i18n.test.ts` (les tres llengües
 * amb les mateixes claus i variables) i `i18nUsage.test.ts` (cada `t('planta.stockCount:…')` existeix). Quan l'anotació explícita de
 * `resources` (commit 64cd075, branca del catàleg) sigui a main, es pot tornar a `as const`.
 */
export const plantaCountEn: { readonly 'planta.stockCount': Readonly<Record<string, string>> } = {
  'planta.stockCount': {
    // ── Entrada i llista de recomptes ─
    title: 'Stock count',
    subtitle: 'Count what is in the workshop. The manager validates the count in the BackOffice.',
    entryTitle: 'Stock count',
    entryBody: 'Scan the lot label and count what is there.',
    entryOpen_one: '{{count}} open count',
    entryOpen_other: '{{count}} open counts',
    entryNone: 'No open count',
    entryUnknown: 'Check the open counts',
    listOpenTitle: 'Open counts',
    listClosedTitle: 'Closed',
    emptyOpen: 'There is no open count',
    emptyOpenHint: 'When the manager creates one in the BackOffice, it will show up here.',
    emptyWeb: 'Counting is done from the workshop app (tablet or phone).',
    refresh: 'Refresh',
    refreshing: 'Refreshing…',
    refreshOffline: 'No connection: the last copy is shown.',
    refreshFailed: 'Could not refresh: the last copy is shown.',
    refreshUnsynced: 'There are counts waiting to be sent: they go first, then it refreshes.',
    refreshPartial: 'Some data could not be read and was left out.',
    scopeTenant: 'The whole workshop',
    scopeNamed_zone: 'Zone · {{name}}',
    scopeNamed_section: 'Section · {{name}}',
    scopeNamed_location: 'Location · {{name}}',
    scopeNamed_article: 'Article · {{name}}',
    scopeType_zone: 'A zone',
    scopeType_section: 'A section',
    scopeType_location: 'A location',
    scopeType_article: 'An article',
    countProgress: '{{counted}} of {{total}} lines counted',
    countNotLoaded: 'Open it to download the lines.',
    photoOn: 'Photo of {{date}}',
    statusOpen: 'Open',
    status_validated: 'Validated',
    status_cancelled: 'Cancelled',
    closedOn: 'Closed on {{date}}',
    unsyncedBadge_one: '{{count}} waiting to be sent',
    unsyncedBadge_other: '{{count}} waiting to be sent',
    rejectedBadge_one: '{{count}} not applied',
    rejectedBadge_other: '{{count}} not applied',
    openMore_one: 'There is {{count}} more open count that is not shown here. Refresh or close one to see it.',
    openMore_other: 'There are {{count}} more open counts that are not shown here. Refresh or close some to see them.',
    openCountLabel: 'Open count {{number}}',
    // ── Pantalla d’un recompte ─
    back: 'Back to the counts',
    missingCount: 'This count is not on the device. Go back to the list and refresh.',
    pendingValidation: 'Pending validation: when you finish, the manager validates it in the BackOffice. Nothing is adjusted here.',
    readOnlyValidated: 'This count was already validated in the BackOffice. It can only be read.',
    readOnlyCancelled: 'This count was cancelled. It can only be read.',
    unsyncedNotice_one: '{{count}} count waiting to be sent. It will go by itself when the connection is back.',
    unsyncedNotice_other: '{{count}} counts waiting to be sent. They will go by themselves when the connection is back.',
    rejectedNotice_one: '{{count}} count not applied: the server could not record it (the count is no longer open or the lot does not fit). The quantity stays here.',
    rejectedNotice_other: '{{count}} counts not applied: the server could not record them (the count is no longer open or the lot does not fit). The quantities stay here.',
    rejectedAck: 'Got it',
    truncatedNotice: 'This count has more lines than the device keeps ({{count}}). Count the rest from the BackOffice.',
    linesFrom: 'Lines downloaded on {{date}}',
    neverPulled: 'The lines of this count have not been downloaded yet. A connection is needed to download them.',
    loadingLines: 'Downloading the lines…',
    noLines: 'This count has no lines.',
    scan: 'Scan',
    addFound: 'Add a lot found',
    scanTitle: 'Scan the lot label',
    scanHint: 'Point at the label QR.',
    filterLabel: 'Filter the lines',
    filterAll: 'All · {{count}}',
    filterPending: 'Pending · {{count}}',
    filterShort: 'Short · {{count}}',
    filterOver: 'Over · {{count}}',
    filterMatch: 'Match · {{count}}',
    searchPlaceholder: 'Search by article, lot or place',
    searchLabel: 'Search a line',
    resultsCount_one: '{{count}} line',
    resultsCount_other: '{{count}} lines',
    emptyNoMatch: 'No line matches “{{query}}”.',
    emptyFilter: 'No line in this state.',
    showMore: 'Show more',
    resultsShown: 'Showing {{shown}} of {{total}}: search or filter to find others.',
    // ── Una línia ─
    lotLine: 'Lot {{lot}}',
    noLot: 'No lot',
    newLot: 'New lot',
    newLotWithCode: 'New lot · {{code}}',
    noPlace: 'No place',
    expiresOn: 'Expires {{date}}',
    photoQty: 'Photo ({{date}}): {{quantity}}',
    countedQty: 'Counted: {{quantity}}',
    notCounted: 'Not counted',
    foundTag: 'Found',
    unsyncedTag: 'Waiting to be sent',
    rejectedTag: 'Not applied',
    state_pending: 'Pending',
    state_match: 'Match',
    state_short: 'Short {{quantity}}',
    state_over: 'Over {{quantity}}',
    lineLabel: '{{article}}, {{state}}',
    // ── El full de quantitat ─
    sheetTitle: 'Count',
    sheetPhoto: 'Photo of {{date}}: {{quantity}}',
    sheetFound: 'Lot found: it was not on the photo.',
    sheetCurrent: 'Already counted: {{quantity}}',
    quantityLabel: 'Quantity counted',
    matchButton: 'Matches',
    matchHelp: 'Counts what the photo says.',
    save: 'Save',
    modeSet: 'Replace',
    modeAdd: 'Add',
    modeAddTotal: 'Total: {{quantity}}',
    invalidQuantity: 'Enter a valid quantity.',
    conflictTitle: 'Someone else already counted it',
    conflictBody: 'There are {{counted}} counted by someone else. What do you want to do with the {{typed}} you typed?',
    conflictAdd: 'Add (total {{total}})',
    conflictReplace: 'Replace ({{typed}})',
    conflictCancel: 'Cancel',
    placesTitle: 'Where is this lot?',
    placesBody: 'Lot {{lot}} is in more than one place of this count. Choose where you count it.',
    // ── Un lot trobat ─
    foundTitle: 'Add a lot found',
    foundBody: 'A lot that was not on the count photo.',
    foundScan: 'Scan a label',
    foundNew: 'New lot (no label)',
    foundNoLine: 'Lot {{lot}} is not in the list of this count.',
    foundAddIt: 'Add it as a lot found',
    foundOutOfScope: 'Lot {{lot}} is {{article}}, but it is outside the scope of this count and cannot be added.',
    foundKnown: '{{article}} · lot {{lot}}',
    foundWhere: 'Where did you find it?',
    foundUnknown: 'We do not know lot {{lot}}. A new one will be created: choose the article.',
    knownLotsChecking: 'Checking lot {{lot}}…',
    knownLotsCannotTitle: 'I cannot check whether the lot already exists',
    knownLotsCannotBody: 'Lot {{lot}} is not in the list this device holds and I cannot tell if it already exists (offline, an old or an incomplete list). You can try again when connected, or count it as new anyway: the scanned number is kept in the note and the manager will review it at validation.',
    knownLotsCountAsNew: 'Count it as new anyway',
    knownLotsCountAsNewHelp: 'The manager will review it at validation.',
    knownLotsRetry: 'Try again',
    scannedLotNote: 'Scanned lot: {{lot}}',
    newLotTitle: 'New lot',
    newLotArticle: 'Article',
    newLotPick: 'Choose the article',
    newLotSearch: 'Search an article by name or code',
    newLotNoArticles: 'This device has no articles yet. Connect it and refresh.',
    newLotNoMatch: 'No article matches “{{query}}”.',
    newLotChange: 'Change the article',
    newLotSupplierCode: 'Supplier lot code (optional)',
    newLotExpiry: 'Expiry',
    newLotExpiryHelp: 'This article asks for an expiry date.',
    newLotQuantity: 'Quantity found',
    newLotHelp: 'The Obrava lot number is created by the server when the count is validated: there is no need to type it.',
    newLotSave: 'Add the lot',
    issue_untracked: 'This article has no stock control: there is nothing to count.',
    issue_production_article: 'This article is produced: a new lot cannot be created from a count. Tell the manager.',
    issue_quantity: 'Enter a quantity greater than zero.',
    issue_expiry_required: 'The expiry date of this article is missing.',
    // ── Missatges ─
    savedLine: 'Counted: {{article}} · {{quantity}}',
    savedMerged: 'This lot already had a line: added to it. {{article}} · +{{quantity}} (total {{total}})',
    errScan: 'The label could not be read. Point at it again.',
    errNotOpen: 'This count is no longer open: it cannot be counted.',
    errProvisionalNewLot: 'This new lot has not been sent yet. Wait for it to sync to change its quantity.',
    errStaleLine: 'This line was updated while you were counting it. Close the sheet and open it again.',
    errSave: 'The count could not be saved. Try again.',
    cannotCount: 'Your role cannot count: you can read the count but not save quantities.',
  },
};
